import { Box, Checkbox, HStack, IconButton, Text } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { CpvNode } from "./cpvHierarchy";

type Props = {
  node: CpvNode;
  isSelected: boolean;
  isExpanded: boolean;
  hasSelectedDescendant: boolean;
  hasChildren: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
};

export const CpvTreeNode = ({
  node,
  isSelected,
  isExpanded,
  hasSelectedDescendant,
  hasChildren,
  onToggleSelect,
  onToggleExpand,
}: Props) => {
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language.startsWith("no");
  const label = isNorwegian ? node.nameNb : node.nameEn;

  const checkboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = !isSelected && hasSelectedDescendant;
    }
  }, [isSelected, hasSelectedDescendant]);

  return (
    <HStack
      spacing="2"
      pl={`${(node.level - 1) * 1.25}rem`}
      py="1"
      align="center"
      borderRadius="sm"
      _hover={{ bg: "smBackgroundSecondary" }}
    >
      {hasChildren ? (
        <IconButton
          aria-label={t(isExpanded ? "Collapse" : "Expand") ?? ""}
          icon={<ChevronRightIcon />}
          size="xs"
          variant="ghost"
          onClick={onToggleExpand}
          transform={isExpanded ? "rotate(90deg)" : "none"}
          transition="transform 0.2s ease"
        />
      ) : (
        <Box w="2rem" />
      )}
      <Checkbox
        ref={checkboxRef}
        colorScheme="teal"
        isChecked={isSelected}
        isIndeterminate={!isSelected && hasSelectedDescendant}
        onChange={onToggleSelect}
        sx={{
          ".chakra-checkbox__control[data-indeterminate]": {
            background: "transparent",
            borderStyle: "dashed",
            borderColor: "teal.500",
            color: "teal.500",
          },
        }}
      />
      <Text
        as="button"
        type="button"
        onClick={onToggleSelect}
        textAlign="left"
        fontFamily="mono"
        fontSize="xs"
        color={isSelected ? "smPrimary" : "smMuted"}
        fontWeight="600"
      >
        {node.code}
      </Text>
      <Text
        as="button"
        type="button"
        onClick={onToggleSelect}
        textAlign="left"
        fontSize="sm"
        color={isSelected ? "smPrimary" : "smText"}
      >
        {label}
      </Text>
    </HStack>
  );
};
